// src/components/Carousel.js
import React, { useContext } from 'react';
import { EventsContext } from '../context/EventsContext';
import { Link } from 'react-router-dom';
import API from '../config/config'; // Updated from '../config'
import logoImage from '../assets/img/logo.png'; // Import the logo

const Carousel = () => {
  const { events, loading, error } = useContext(EventsContext);
  
  const formatEventDateTime = (dateString) => {
    if (!dateString) return 'Date not available';
    try {
      const eventDate = new Date(dateString);
      return eventDate.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (e) {
      console.error('Date formatting error:', e);
      return dateString;
    }
  };

  // Add helper function for time formatting
  const formatTimeDisplay = (time) => {
    if (!time || time === 'null' || time === 'undefined' || time === '' || time === 'N/A') {
      return null;
    }
    
    try {
      // If it's already in HH:MM format, convert to 12-hour format
      if (time.match(/^\d{1,2}:\d{2}$/)) {
        const [hours, minutes] = time.split(':');
        const hour24 = parseInt(hours);
        const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
        const ampm = hour24 >= 12 ? 'PM' : 'AM';
        return `${hour12}:${minutes} ${ampm}`;
      }
      
      return null;
    } catch (error) {
      return null;
    }
  };

  // Handle loading and error states
  if (loading) {
    return (
      <div className="carousel-loading d-flex justify-content-center align-items-center" style={{ height: '600px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !events || events.length === 0) {
    return (
      <div className="carousel-error d-flex justify-content-center align-items-center" style={{ height: '600px' }}>
        <div className="text-center">
          <h3 className="text-danger">Unable to load featured events</h3>
          <p>Please check back later</p>
        </div>
      </div>
    );
  }

  const limitedEvents = events.slice(0, 8); // Limit to 8 events for the carousel

  return (
    <>
      <div id="carouselExampleCaptions" className="carousel slide" data-bs-ride="carousel">
        {/* Carousel Indicators */}
        <div className="carousel-indicators">
          {limitedEvents.map((_, index) => (
            <button
              type="button"
              key={index}
              data-bs-target="#carouselExampleCaptions"
              data-bs-slide-to={index}
              className={index === 0 ? 'active' : ''}
              aria-label={`Slide ${index + 1}`}
            ></button>
          ))}
        </div>

        {/* Carousel Items */}
        <div className="carousel-inner">
          {limitedEvents.map((event, index) => (
            <div key={event.id} className={`carousel-item ${index === 0 ? 'active' : ''}`}>
              <img
                src={event.event_graphic ? `${API.UPLOADS}/${event.event_graphic}` : logoImage}
                className="d-block w-100"
                alt={event.event_title}
                style={{ height: '900px', objectFit: 'cover' }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = logoImage;
                }}
              />
              <div className="carousel-caption d-flex flex-column justify-content-center align-items-center h-100">
                <div className="carousel-content col-12 col-md-6 mx-auto text-center">
                  {/* Event Title */}
                  <h1 className="font_60">
                    {event.event_title}
                  </h1>

                  {/* Event Details */}
                  <p className="mt-3">{event.event_summary}</p>
                  <div className="carousel-details">
                    <p className="mb-2">
                      <span className="col_red me-1 fw-bold">Location:</span> {event.location}
                    </p>
                    <p className="mb-2">
                      <span className="col_red me-1 fw-bold">Date:</span> {formatEventDateTime(event.event_date)}
                    </p>
                    {event.business_name && (
                      <p className="mb-2">
                        <span className="col_red me-1 fw-bold">Hosted by:</span> {event.business_name}
                      </p>
                    )}
                    {/* Simplified time display logic */}
                    <p className="mb-2">
                      <span className="col_red me-1 fw-bold">Time:</span> 
                      {formatTimeDisplay(event.event_time) || event.event_time || 'TBD'}
                      {event.event_end_time && (
                        <> - {formatTimeDisplay(event.event_end_time) || event.event_end_time}</>
                      )}
                    </p>
                  </div>

                  {/* More Info Link */}
                  <div className="text-center">
                    <h6 className="mt-4 mb-0">
                      <Link className="button" to={`/event/${event.id}`}>
                        <i className="fa fa-info-circle me-1"></i> More Info
                      </Link>
                    </h6>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Carousel Controls */}
        <button
          className="carousel-control-prev"
          type="button"
          data-bs-target="#carouselExampleCaptions"
          data-bs-slide="prev"
        >
          <span className="carousel-control-prev-icon" aria-hidden="true"></span>
          <span className="visually-hidden">Previous</span>
        </button>
        <button
          className="carousel-control-next"
          type="button"
          data-bs-target="#carouselExampleCaptions"
          data-bs-slide="next"
        >
          <span className="carousel-control-next-icon" aria-hidden="true"></span>
          <span className="visually-hidden">Next</span>
        </button>
      </div>
      {/* Add this updated CSS at the end of the component for better mobile styling */}
      <style jsx="true">{`
        .carousel-caption {
          /* Remove top/bottom positioning to use flex centering */
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          padding-top: 0;
          padding-bottom: 0;
        }
        
        .carousel-content {
          padding: 15px;
          /* Add a semi-transparent background for better text visibility */
          background-color: rgba(0, 0, 0, 0.3);
          border-radius: 10px;
        }
        
        @media (max-width: 768px) {
          .font_60 {
            font-size: 2rem;
            margin-bottom: 10px;
          }
          
          .carousel-details {
            font-size: 0.85rem;
          }
          
          .carousel-content {
            /* Make the content box slightly narrower on mobile */
            width: 90%;
            padding: 10px;
            /* Center the content vertically but move it down slightly from the top */
            margin-top: 25%;
            margin-bottom: 25%;
          }
          
          .carousel-caption p {
            margin-bottom: 0.5rem;
          }
          
          /* Make the More Info button smaller on mobile */
          .carousel-caption .button {
            padding: 6px 14px;
            font-size: 0.9rem;
          }
        }
      `}</style>
    </>
  );
};

export default Carousel;
